import type { Contact, OpportunityContactRole } from '@/apiTypes'
import Plan from '@/classes/Plan'
import AppointmentImporter, {
	type SessionImport,
} from '@/classes/importers/AppointmentImporter'
import type { PlanData, SessionData } from '@/types'

/** Class to import data from an appointment into the session builder */
export default class SalesforceAppointmentImporter extends AppointmentImporter {
	private appointmentId: string

	public constructor(appointmentId: string) {
		super()
		this.appointmentId = appointmentId
	}

	public async import(): Promise<SessionImport> {
		const sessionData: Partial<SessionData> = {}
		const plans: Partial<PlanData>[] = []

		const appt = await this.getAppointmentById(this.appointmentId)

		/** Session-wide data */
		sessionData.appointmentId = null
		sessionData.salesforceEventAppointmentId = appt.id

		// TODO: PSS-1946 see any adjustment is needed in Session serialization, and Session upsert request when funeralHome is set on appt
		if (appt.funeralHome?.data.groupId) {
			sessionData.crmFhGroupId = appt.funeralHome.data.groupId
		}

		if (appt.funeralHome?.data.salesforceId) {
			sessionData.FuneralHomeLocationId = appt.funeralHome.data.salesforceId
		}

		/** Plan data */
		const lead = appt.lead?.data

		// For now we still use lead for the old CRM ID, update if that changes
		if (!lead) {
			console.error('Loaded appointment has no lead')
			return { session: sessionData, plans, planPrototype: {} }
		}

		const planPrototype: Partial<PlanData> = {
			leadId: lead.id ?? '', // PlanData has always made leadId required, but defaults it to '' in the class
			opportunityId: appt.opportunityId,
		}

		if (!appt.contactRoles) {
			console.error('Loaded appointment has no contacts')
			return { session: sessionData, plans, planPrototype }
		}

		plans.push(
			...this.getPlansFromContactRoles(appt.contactRoles, appt.opportunityId)
		)

		return { session: sessionData, plans, planPrototype }
	}

	public getPlansFromContactRoles(
		contactRoles: OpportunityContactRole[],
		opportunityId?: string
	): Plan[] {
		return contactRoles.map<Plan>(
			(contact) =>
				new Plan(this.mapContactToPlanData(contact.contact.data, opportunityId))
		)
	}

	public mapContactToPlanData(
		contact: Contact,
		opportunityId?: string
	): Partial<PlanData> {
		return {
			firstName: contact.firstName,
			lastName: contact.lastName,
			contactId: contact.id,
			leadId: contact.fkMscrmId,
			opportunityId,
			address: contact.mailingAddress.street,
			city: contact.mailingAddress.city,
			state: contact.mailingAddress.state,
			zip: contact.mailingAddress.postalCode,
			email: contact.email,
			phone: contact.phone,
		}
	}
}
